import { Component, Input, OnInit } from '@angular/core';

import { TreeNode } from 'primeng/api';
import { cloneDeep } from 'lodash';

import { AbstractFilterTypeComponent } from '../../../../Common/Filter/models/abstract-filter-type.component';
import { FilterOptionModel } from '../../../../Common/Filter/models/filter-option.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { mediaTypesWithSubTypesMap, MediaTypeTreeFilterModel } from '../../../../Common/Filter/models/media-type-tree-filter.model';
import { MediaTypeModel } from '../../../../Monitor/Shared/models/media-type.model';
import { MediaTypeEnum } from '../../../Basic/enums/media-type.enum';
import { SelectedMediaTypesFilterModel } from '../../../../Monitor/Shared/models/selected-media-types-filter.model';
import { TreeListTypesEnum } from '../../../../Common/Forms/enums/tree-list-types.enum';

@Component({
    selector: 'agpr-media-type-tree-filter',
    templateUrl: './media-type-tree-filter.component.html',
    styleUrls: ['./media-type-tree-filter.component.scss'],
    standalone: false
})
export class MediaTypeTreeFilterComponent extends AbstractFilterTypeComponent implements OnInit {

    @Input() declare filter: MediaTypeTreeFilterModel;

    // tree structured treeNode array containing all possible selections
    filterOptions: TreeNode<FilterOptionModel<MediaTypeModel>>[] = [];

    // flat structured treeNode array containing individual treeNode selections
    selectedOptions: TreeNode<FilterOptionModel<MediaTypeModel>>[] = [];

    value: SelectedMediaTypesFilterModel = new SelectedMediaTypesFilterModel([], false);

    archivedOnly: boolean;
    anyOptionsSelected: boolean = false;
    showAdditionalOptions: boolean = false;

    additionalOptions: FilterOptionModel[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly treeListTypesEnum = TreeListTypesEnum;

    constructor() {
        super();
    }

    ngOnInit() {
        this.refresh();
    }

    /**
     * Refreshes the current value of the filter
     */
    refresh() {
        this.search();
        this.value = this.filter.getCurrentValue();

        const treeNodes = [];

        this.value.mediaTypes?.forEach(media => {
            const filterTreeNode = this.filterOptions.find(x => x.data.value.id === media.id);
            if (mediaTypesWithSubTypesMap.has(filterTreeNode?.data.value.id) && media?.children?.length) {
                if (mediaTypesWithSubTypesMap.get(filterTreeNode.data.value.id).length === media.children.length) {
                    treeNodes.push(filterTreeNode);
                    treeNodes.push(...filterTreeNode.children);
                } else {
                    treeNodes.push(...filterTreeNode.children.filter(child =>
                        media.children.map(x => x.value.childId).includes(child.data.value.childId))
                    );
                }
            } else if (filterTreeNode) {
                treeNodes.push(filterTreeNode);
            }
        });
        this.selectedOptions = treeNodes;

        mediaTypesWithSubTypesMap.forEach((value, key) => {
            this.updateParentOptionState(key);
        });
        this.archivedOnly = this.value.archivedOnly;
        this.anyOptionsSelected = this.selectedOptions.length > 0;
        this.showAdditionalBroadCastOptions();
    }

    updateParentOptionState(mediaType: MediaTypeEnum) {
        const parent = this.filterOptions.find(x => x.data.value.id === mediaType && x.data.value.childId === undefined);
        if (parent) {
            // update parent to partially selected if children in selectedOptions
            const hasSelectedChildren = this.selectedOptions.filter(x => x.data.value.id === mediaType).length > 0;
            parent.partialSelected = hasSelectedChildren;
            parent.expanded = hasSelectedChildren;
        }
    }

    /**
     * Updates the current value of the filter
     */
    handleChange() {
        this.showAdditionalBroadCastOptions();
        this.value.mediaTypes = this.treeNodeToFilterOptions(this.selectedOptions);
        this.value.archivedOnly = this.archivedOnly;
        this.filter.setCurrentValue(this.value);
        this.anyOptionsSelected = this.selectedOptions.length > 0;
    }

    selectItems() {
        if (!this.anyOptionsSelected) {
            /**
             * Selects all of the options
             */
            // @hack have to use exact filterOptions reference, so clean once complete
            const allOptionsFlat = this.filterOptions;

            if (allOptionsFlat.find(x => mediaTypesWithSubTypesMap.has(x.data.value.id))) {
                allOptionsFlat.push(...allOptionsFlat.find(x => mediaTypesWithSubTypesMap.has(x.data.value.id)).children);
            }
            this.selectedOptions = allOptionsFlat;
            this.filterOptions = this.filterOptions.filter(x => x.data.value.childId === undefined);
            this.handleChange();
        } else {
            /**
             * Deselects all of the options
             */
            this.selectedOptions = [];
            this.filterOptions.forEach(x => x.partialSelected = false);
            this.handleChange();
        }
    }

    /**
     * Filters the visible options based on the search query
     */
    search() {
        this.filterOptions = this.filterOptionToTreeNode(this.filter.options);
        this.showAdditionalBroadCastOptions();
    }


    onNodeSelcted(event) {
        event.node.expanded = true;
    }

    onNodeUnselect(event) {
        event.node.expanded = false;
    }

    /** Helper function for converting filter options to TreeNode */
    filterOptionToTreeNode(options: FilterOptionModel<MediaTypeModel>[]): TreeNode<FilterOptionModel<MediaTypeModel>>[] {
        const treeNodes: TreeNode<FilterOptionModel>[] = [];
        options?.forEach(filter => {
            treeNodes.push({
                label: filter.label,
                data: filter,
                expanded: false,
                type: 'count',
                parent: undefined,
                partialSelected: false,
                children: mediaTypesWithSubTypesMap.has(filter.value?.id) ? this.filterOptionToTreeNode(filter.value.children) : []
            });
        });
        return treeNodes;
    }

    /**
     * Helper function for converting TreeNode to media model
     * @param treeNodes array of TreeNodes in a flat structure, only includes parent if all children selected
     * @returns MediaTypeModel structure with children prop
    */
    treeNodeToFilterOptions(treeNodes: TreeNode<FilterOptionModel<MediaTypeModel>>[]): MediaTypeModel[] {
        const mediaTypes: MediaTypeModel[] = [];
        // only add nodes that are not children
        treeNodes?.filter(node => node.data.value.childId === undefined).forEach(node => {
            // clone so mutating parent.children below doesn't corrupt the shared MediaTypeModel instance living in filter.options
            mediaTypes.push(node.data.value.clone());
        });

        mediaTypesWithSubTypesMap.forEach((val, key) => {
            const parent = mediaTypes.find(x => x.id === key);
            const children = treeNodes.filter(node => {
                return (node.data.value.id === key && node.data.value.childId !== undefined);
            }).map(x => x.data);
            if (parent) {
                parent.children = children;
            } else if (children.length > 0) {
                const newParent = cloneDeep(this.filter.options.find(x => x.value.id === key))?.value;
                newParent.children = children;
                mediaTypes.push(newParent);
            }
        });

        return mediaTypes;
    }

    /**
     * Shows additional options for broadcast mediatype if one is selected
     */
    showAdditionalBroadCastOptions() {
        if (!this.selectedOptions) {
            return;
        }

        if (!this.filter.allowArchivedBroadcastFilter) {
            this.archivedOnly = false;

            return;
        }

        this.showAdditionalOptions = !!this.selectedOptions.map(x => x.data.value).find(mediaType => {
            if (
                mediaType.id === MediaTypeEnum.TV
                || mediaType.id === MediaTypeEnum.RADIO
            ) {
                return true;
            }

            return false;
        });

        if (!this.showAdditionalOptions) {
            this.archivedOnly = false;
        }
    }

    currentIncludeExcludeChange(): void {
        this.archivedOnly = false;
        this.handleChange();
        this.filter.setIncludeExclude(this.filter.currentIncludeExclude);
    }
}
